import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings, Mail, Save, TestTube, ArrowLeft, Check, X, Menu, Crown, Image, BookOpen, Search, Shield, Download, Clock, Files, Lock, CalendarClock, CreditCard, Eye, EyeOff, Zap, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'SENDBOX',
    use_tls: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [isConfigured, setIsConfigured] = useState(false);
  const [offsetGb, setOffsetGb] = useState(0);
  const [savingOffset, setSavingOffset] = useState(false);
  const [planLimits, setPlanLimits] = useState({
    expiry_days_free: 7,
    expiry_days_pro: 30,
    expiry_days_ultimate: 60,
    max_downloads_free: 10,
    max_downloads_pro: 0,
    max_downloads_ultimate: 0,
    max_files_free: 5,
    max_files_pro: 0,
    max_files_ultimate: 0,
    max_uploads_per_hour_free: 5,
    password_protection_free: false,
    scheduled_send_free: false,
    multipart_threshold_mb: 5,
  });
  const [savingLimits, setSavingLimits] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [stripeSource, setStripeSource] = useState('none');
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
    loadFooterStats();
    loadPlanLimits();
    loadStripeStatus();
  }, []);

  const loadStripeStatus = async () => {
    try {
      const token = localStorage.getItem('sendbox_token');
      const res = await axios.get(`${API}/admin/settings/stripe`, { headers: { Authorization: `Bearer ${token}` } });
      setStripeConfigured(res.data.configured);
      setStripeSource(res.data.source || 'none');
    } catch (e) {
      console.error('Error loading Stripe status:', e);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/smtp`);
      if (res.data.configured) {
        setSmtpSettings(prev => ({
          ...prev,
          ...res.data,
          smtp_password: '' // Don't load password
        }));
        setIsConfigured(true);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFooterStats = async () => {
    try {
      const token = localStorage.getItem('sendbox_token');
      const res = await axios.get(`${API}/admin/footer-stats`, { headers: { Authorization: `Bearer ${token}` } });
      setOffsetGb(res.data.offset_gb || 0);
    } catch (e) {
      console.error('Error loading footer stats:', e);
    }
  };

  const saveOffset = async () => {
    setSavingOffset(true);
    try {
      const token = localStorage.getItem('sendbox_token');
      await axios.put(`${API}/admin/footer-stats`, { offset_gb: offsetGb }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Offset sauvegardé', description: `${offsetGb} Go ajoutés au compteur` });
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSavingOffset(false);
    }
  };

  const loadPlanLimits = async () => {
    try {
      const token = localStorage.getItem('sendbox_token');
      const res = await axios.get(`${API}/admin/plan-limits`, { headers: { Authorization: `Bearer ${token}` } });
      setPlanLimits(prev => ({ ...prev, ...res.data }));
    } catch (e) {
      console.error('Error loading plan limits:', e);
    }
  };

  const savePlanLimits = async () => {
    setSavingLimits(true);
    try {
      const token = localStorage.getItem('sendbox_token');
      const res = await axios.put(`${API}/admin/plan-limits`, planLimits, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        toast({ title: 'Limites sauvegardées', description: 'Les limites Free/Pro ont été mises à jour' });
      }
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder les limites', variant: 'destructive' });
    } finally {
      setSavingLimits(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/smtp`, smtpSettings);
      toast({
        title: 'Paramètres enregistrés',
        description: 'La configuration SMTP a été sauvegardée',
      });
      setIsConfigured(true);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de sauvegarder',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast({
        title: 'Email requis',
        description: 'Entrez une adresse email pour le test',
        variant: 'destructive',
      });
      return;
    }
    
    setTesting(true);
    try {
      await axios.post(`${API}/admin/settings/smtp/test?test_email=${encodeURIComponent(testEmail)}`);
      toast({
        title: 'Email envoyé !',
        description: `Un email de test a été envoyé à ${testEmail}`,
      });
    } catch (error) {
      toast({
        title: 'Échec du test',
        description: error.response?.data?.detail || 'Impossible d\'envoyer l\'email de test',
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col">
      <Toaster />
      <NavigationMenu />

      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard?tab=settings" className="text-blue-400 hover:text-blue-300 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-blue-400" />
                <h1 className="text-xl font-semibold text-white">Limites & Paramètres</h1>
              </div>
            </div>
            <span className="text-sm text-blue-300 bg-blue-500/10 px-3 py-1 rounded-full">
              Configuration
            </span>
          </div>

        <div className="space-y-6">
        {/* Plan Limits Card */}
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden" data-testid="plan-limits-section">
          <div className="p-6 border-b border-blue-500/20">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Shield className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Limites des plans</h2>
                <p className="text-sm text-blue-300/70">Configurer les limites pour les utilisateurs Free et Pro</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Free Tier */}
            <div>
              <h3 className="text-sm font-semibold text-blue-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                Plan Gratuit (Free)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-blue-300 mb-2 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Expiration (jours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={planLimits.expiry_days_free}
                    onChange={e => setPlanLimits({ ...planLimits, expiry_days_free: parseInt(e.target.value) || 1 })}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    data-testid="expiry-days-free-input"
                  />
                  <p className="text-xs text-blue-300/40 mt-1">Nombre de jours avant suppression automatique</p>
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2 flex items-center gap-1.5">
                    <Download className="w-3.5 h-3.5" /> Téléchargements max
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={planLimits.max_downloads_free}
                    onChange={e => setPlanLimits({ ...planLimits, max_downloads_free: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    data-testid="max-downloads-free-input"
                  />
                  <p className="text-xs text-blue-300/40 mt-1">0 = illimité</p>
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2 flex items-center gap-1.5">
                    <Files className="w-3.5 h-3.5" /> Fichiers max par envoi
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={planLimits.max_files_free}
                    onChange={e => setPlanLimits({ ...planLimits, max_files_free: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    data-testid="max-files-free-input"
                  />
                  <p className="text-xs text-blue-300/40 mt-1">0 = illimité</p>
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Uploads max / heure
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={planLimits.max_uploads_per_hour_free}
                    onChange={e => setPlanLimits({ ...planLimits, max_uploads_per_hour_free: parseInt(e.target.value) || 1 })}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    data-testid="max-uploads-hour-free-input"
                  />
                  <p className="text-xs text-blue-300/40 mt-1">Limite de débit par heure pour les utilisateurs gratuits</p>
                </div>
              </div>

              {/* Free user feature toggles */}
              <div className="mt-4 pt-4 border-t border-blue-500/10">
                <h4 className="text-sm font-medium text-blue-300/70 mb-3">Fonctionnalités optionnelles (Gratuit)</h4>
                <div className="space-y-3">
                  <label className="flex items-center justify-between cursor-pointer group" data-testid="password-protection-free-toggle">
                    <div className="flex items-center gap-3">
                      <Lock className="w-4 h-4 text-blue-400/70" />
                      <span className="text-sm text-blue-200">Protection par mot de passe</span>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={planLimits.password_protection_free}
                        onChange={e => setPlanLimits({ ...planLimits, password_protection_free: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </div>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer group" data-testid="scheduled-send-free-toggle">
                    <div className="flex items-center gap-3">
                      <CalendarClock className="w-4 h-4 text-blue-400/70" />
                      <span className="text-sm text-blue-200">Programmation d'envoi</span>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={planLimits.scheduled_send_free}
                        onChange={e => setPlanLimits({ ...planLimits, scheduled_send_free: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Performance settings (applies to all users) */}
            <div className="pt-4 border-t border-blue-500/10">
              <h3 className="text-sm font-semibold text-cyan-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Settings className="w-4 h-4 text-cyan-400" />
                Performance Upload (tous les utilisateurs)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-blue-300 mb-2">
                    Seuil multipart Wasabi (Mo)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="500"
                    value={planLimits.multipart_threshold_mb}
                    onChange={e => setPlanLimits({ ...planLimits, multipart_threshold_mb: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-900/50 border border-cyan-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
                    data-testid="multipart-threshold-input"
                  />
                  <p className="text-xs text-blue-300/40 mt-1">
                    Les fichiers ≥ ce seuil utilisent l'upload direct vers Wasabi (sans disque local). Multi-fichiers : toujours actif. <strong className="text-cyan-300">Recommandé : 5</strong>. Mettre 0 pour forcer Wasabi sur TOUS les uploads.
                  </p>
                </div>
              </div>
            </div>

            {/* Pro Tier */}
            <div className="pt-4 border-t border-blue-500/10">
              <h3 className="text-sm font-semibold text-yellow-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Crown className="w-4 h-4 text-yellow-400" />
                Plan Pro
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-blue-300 mb-2 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Expiration (jours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={planLimits.expiry_days_pro}
                    onChange={e => setPlanLimits({ ...planLimits, expiry_days_pro: parseInt(e.target.value) || 1 })}
                    className="w-full bg-slate-900/50 border border-yellow-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/40"
                    data-testid="expiry-days-pro-input"
                  />
                  <p className="text-xs text-blue-300/40 mt-1">Nombre de jours avant suppression automatique</p>
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2 flex items-center gap-1.5">
                    <Download className="w-3.5 h-3.5" /> Téléchargements max
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={planLimits.max_downloads_pro}
                    onChange={e => setPlanLimits({ ...planLimits, max_downloads_pro: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-900/50 border border-yellow-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/40"
                    data-testid="max-downloads-pro-input"
                  />
                  <p className="text-xs text-blue-300/40 mt-1">0 = illimité</p>
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2 flex items-center gap-1.5">
                    <Files className="w-3.5 h-3.5" /> Fichiers max par envoi
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={planLimits.max_files_pro}
                    onChange={e => setPlanLimits({ ...planLimits, max_files_pro: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-900/50 border border-yellow-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/40"
                    data-testid="max-files-pro-input"
                  />
                  <p className="text-xs text-blue-300/40 mt-1">0 = illimité</p>
                </div>
              </div>
            </div>

            {/* Ultimate Tier */}
            <div className="pt-4 border-t border-blue-500/10">
              <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4 text-purple-400" />
                Plan Ultimate
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-blue-300 mb-2 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Expiration (jours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={planLimits.expiry_days_ultimate}
                    onChange={e => setPlanLimits({ ...planLimits, expiry_days_ultimate: parseInt(e.target.value) || 1 })}
                    className="w-full bg-slate-900/50 border border-purple-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                    data-testid="expiry-days-ultimate-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2 flex items-center gap-1.5">
                    <Download className="w-3.5 h-3.5" /> Telechargements max
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={planLimits.max_downloads_ultimate}
                    onChange={e => setPlanLimits({ ...planLimits, max_downloads_ultimate: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-900/50 border border-purple-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                    data-testid="max-downloads-ultimate-input"
                  />
                  <p className="text-xs text-blue-300/40 mt-1">0 = illimite</p>
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2 flex items-center gap-1.5">
                    <Files className="w-3.5 h-3.5" /> Fichiers max par envoi
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={planLimits.max_files_ultimate}
                    onChange={e => setPlanLimits({ ...planLimits, max_files_ultimate: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-900/50 border border-purple-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                    data-testid="max-files-ultimate-input"
                  />
                  <p className="text-xs text-blue-300/40 mt-1">0 = illimite</p>
                </div>
              </div>
              <p className="text-xs text-purple-300/40 mt-3">Les utilisateurs Ultimate beneficient aussi du transfert P2P direct (WebRTC) sans limite de taille.</p>
            </div>

            {/* Save button */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={savePlanLimits}
                disabled={savingLimits}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
                data-testid="save-plan-limits-btn"
              >
                {savingLimits ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Enregistrement...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" /> Enregistrer les limites
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Stripe Configuration Card - Link to dedicated page */}
        <div className="bg-slate-800/50 border border-purple-500/20 rounded-2xl overflow-hidden" data-testid="stripe-settings-section">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <CreditCard className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-white">Configuration Stripe</h2>
                <p className="text-sm text-blue-300/70">Configurer les paiements et tarification Pro/Ultimate</p>
              </div>
              {stripeConfigured && (
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full flex items-center gap-1">
                  <Check className="w-3 h-3" /> Configuré ({stripeSource})
                </span>
              )}
              <Link 
                to="/admin/stripe" 
                className="flex items-center gap-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 px-4 py-2 rounded-xl transition-colors"
                data-testid="stripe-config-link"
              >
                Configurer <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-6 p-5 bg-blue-500/10 border border-blue-500/20 rounded-xl">
          <h3 className="text-sm font-medium text-blue-300 mb-2">Informations</h3>
          <ul className="text-sm text-blue-300/70 space-y-1">
            <li>- Les modifications des limites sont appliquées immédiatement</li>
            <li>- 0 = illimité pour les champs numériques Pro</li>
            <li>- La configuration SMTP se trouve dans la rubrique dédiée "SMTP"</li>
          </ul>
        </div>
        </div>
        </div>
      </main>
      
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default AdminSettings;
